"use client";

import { useMemo, useState, type FormEvent } from "react";
import type { FacturationExclusionRule } from "@/prisma/generated/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addFacturationExclusionRule,
  editFacturationExclusionRule,
} from "@/app/lib/actions/facturation-exclusion-rules";
import { useAakRulesQuery } from "@/lib/queries";

type FormValues = {
  brandNumericCode: string;
  brandAlphabeticCode: string;
  brandAlphabeticLabel: string;
  saleNature: string;
  billingCode: FacturationExclusionRule["billingCode"];
  effectiveStartDate: string;
  effectiveEndDate: string;
  versionNote?: string;
};

const DEFAULT_FORM: FormValues = {
  brandNumericCode: "",
  brandAlphabeticCode: "",
  brandAlphabeticLabel: "",
  saleNature: "",
  billingCode: "FDM",
  effectiveStartDate: "",
  effectiveEndDate: "",
  versionNote: "",
};

function toDateInputValue(value: Date | null) {
  if (!value) return "";
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function resolveErrorMessage(result: {
  message?: string;
  fieldErrors?: Record<string, string[]> | string;
}) {
  if (result.message) return result.message;
  if (typeof result.fieldErrors === "string") return result.fieldErrors;
  const firstError = Object.values(result.fieldErrors ?? {}).find(
    (messages) => messages && messages.length > 0,
  );
  return firstError?.[0] ?? "Erreur de validation";
}

export default function FacturationExclusionRuleForm({
  rule,
  open,
  onClose,
  onSuccess,
}: {
  rule: FacturationExclusionRule | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isNew = !rule;
  const [form, setForm] = useState<FormValues>(() =>
    rule
      ? {
          brandNumericCode: rule.brandNumericCode,
          brandAlphabeticCode: rule.brandAlphabeticCode,
          brandAlphabeticLabel: rule.brandAlphabeticLabel,
          saleNature: rule.saleNature,
          billingCode: rule.billingCode,
          effectiveStartDate: toDateInputValue(rule.effectiveStartDate),
          effectiveEndDate: toDateInputValue(rule.effectiveEndDate),
          versionNote: "",
        }
      : DEFAULT_FORM,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: aakData } = useAakRulesQuery(
    { sort: { saleNature: "asc" } },
    undefined,
    { enabled: open },
  );

  const saleNatures = useMemo(
    () =>
      (aakData?.aakRules ?? []).map((rule) => ({
        value: rule.saleNature,
        label: `${rule.saleNature} - ${rule.label}`,
      })),
    [aakData],
  );

  const set = (key: keyof FormValues) => (value: unknown) =>
    setForm((previous) => ({ ...previous, [key]: value }) as FormValues);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const payload = {
      ...(isNew ? {} : { id: rule.id }),
      brandNumericCode: form.brandNumericCode.trim(),
      brandAlphabeticCode: form.brandAlphabeticCode.trim().toUpperCase(),
      brandAlphabeticLabel: form.brandAlphabeticLabel.trim().toUpperCase(),
      saleNature: form.saleNature.trim().toUpperCase(),
      billingCode: form.billingCode,
      effectiveStartDate: form.effectiveStartDate,
      effectiveEndDate: form.effectiveEndDate || null,
      versionNote: form.versionNote || undefined,
      isActive: true,
    };

    const result = isNew
      ? await addFacturationExclusionRule(payload)
      : await editFacturationExclusionRule(payload);

    setLoading(false);

    if (!result.success) {
      setError(resolveErrorMessage(result));
      return;
    }

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? "Nouvelle exclusion de facturation"
              : `Modifier - ${rule?.brandAlphabeticCode} - ${rule?.saleNature} - ${rule?.billingCode}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          ) : null}

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Code marque numérique</Label>
              <Input
                value={form.brandNumericCode}
                onChange={(e) => set("brandNumericCode")(e.target.value)}
                placeholder="1"
                required
                maxLength={1}
                inputMode="numeric"
                className="font-mono tracking-widest"
              />
            </div>

            <div className="space-y-2">
              <Label>Code marque alphabétique</Label>
              <Input
                value={form.brandAlphabeticCode}
                onChange={(e) =>
                  set("brandAlphabeticCode")(e.target.value.toUpperCase())
                }
                placeholder="V"
                required
                maxLength={1}
                className="font-mono tracking-widest uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Code facturation</Label>
              <Select
                value={form.billingCode}
                onValueChange={(value) => set("billingCode")(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["FDM", "VN", "FU", "VO", "DA", "CS"] as const).map(
                    (code) => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Libellé marque alphabétique</Label>
            <Input
              value={form.brandAlphabeticLabel}
              onChange={(e) =>
                set("brandAlphabeticLabel")(e.target.value.toUpperCase())
              }
              placeholder="VOLKSWAGEN"
              required
              maxLength={20}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nature de vente</Label>
              <Select
                value={form.saleNature}
                onValueChange={(value) => set("saleNature")(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une nature de vente" />
                </SelectTrigger>
                <SelectContent>
                  {saleNatures.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date début d&apos;application</Label>
              <Input
                type="date"
                value={form.effectiveStartDate}
                onChange={(e) => set("effectiveStartDate")(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Date fin d&apos;application (optionnel)</Label>
            <Input
              type="date"
              value={form.effectiveEndDate}
              onChange={(e) => set("effectiveEndDate")(e.target.value)}
            />
          </div>

          {!isNew ? (
            <div className="space-y-2">
              <Label>
                Note de modification{" "}
                <span className="text-muted-foreground font-normal">
                  (optionnel)
                </span>
              </Label>
              <Input
                value={form.versionNote ?? ""}
                onChange={(e) => set("versionNote")(e.target.value)}
                placeholder="Motif de la modification..."
                disabled={loading}
              />
            </div>
          ) : null}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement..." : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
